/* eslint-disable class-methods-use-this */
import React, { createRef } from 'react';
import store from 'store/Store';
import FrameworkUtils from 'utils/framework';
import { css, Global } from '@emotion/react';
import { Icon } from 'components/ui';
import scriptUtil from 'utils/LoadScripts';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import mediaUtils from 'utils/Media';
import LanguageLocale from 'utils/LanguageLocale';
import {
    colors, mediaQueries, fontSizes, lineHeights, fontWeights, space, radii
} from 'style/config';
import Storage from 'utils/localStorage/Storage';
import Location from 'utils/Location';
import anaConsts from 'analytics/constants';
import processEvent from 'analytics/processEvent';
import { CHAT_ENTRY } from 'constants/chat';
import BaseClass from 'components/BaseClass/BaseClass';

const { wrapComponent } = FrameworkUtils;
const { Media } = mediaUtils;
const { getLocaleResourceFile } = LanguageLocale;

const getText = getLocaleResourceFile('components/GladChat/ChatButton/locales', 'ChatButton');
class GladChat extends BaseClass {
    constructor(props) {
        super(props);
        this.state = {
            unreadMessage: 0
        };
        this.gladRef = createRef();
        this.conversationTimeout = null;
    }

    componentDidMount() {
        const { isChatOpen = false, conversationEnd, unreadMessage } = this.getStorageItem(LOCAL_STORAGE.GLADLY_CHAT);
        this.props.updateGladChatState({ isChatOpen });

        if (unreadMessage) {
            this.setState({ unreadMessage });
        }

        if (!window.Gladly) {
            scriptUtil.loadScripts(['/js/ufe/isomorphic/thirdparty/gladchat.js'], () => {
                const { appId } = Sephora.configurationSettings.gladChatConfig;

                if (window.Gladly && appId) {
                    this.initializeGladChat(appId, isChatOpen, conversationEnd);
                }
            });
        }

        window.addEventListener('popstate', this.handleClose);
    }

    componentWillUnmount() {
        window.removeEventListener('popstate', this.handleClose);
    }

    getStorageItem = key => {
        return Storage.local?.getItem(key) || {};
    };

    setStorageItem = (value, key) => {
        const gladChat = this.getStorageItem(key);
        Storage.local.setItem(key, { ...gladChat, ...value });
    };

    fireAnalytics = (eventData, callType) => {
        processEvent.process(callType, {
            data: eventData
        });
    };

    afterInitilizeGladChat = () => {
        store.setAndWatch('gladChat', this, data => {
            const current = this.getStorageItem(LOCAL_STORAGE.GLADLY_CHAT);

            if (data.gladChat.clickPage) {
                let eventData = null;
                let callType = null;

                switch (data.gladChat.clickPage) {
                    case CHAT_ENTRY.footer:
                        eventData = {
                            pageName: `${anaConsts.PAGE_NAMES.GLAD_FOOTER_PAGE}:n/a:*`,
                            eventStrings: [anaConsts.Event.EVENT_220],
                            linkData: anaConsts.LinkData.CUSTOMER_CHAT_SESSION_STARTED,
                            navigationInfo: `toolbar nav:${anaConsts.PAGE_DETAIL.GLAD_PAGE}: ${anaConsts.PAGE_DETAIL.GLAD_PAGE}: ${anaConsts.PAGE_DETAIL.GLAD_PAGE}: ${anaConsts.PAGE_DETAIL.GLAD_PAGE}`
                        };
                        callType = anaConsts.ASYNC_PAGE_LOAD;

                        break;
                    case CHAT_ENTRY.customerService:
                        eventData = {
                            pageName: `${anaConsts.PAGE_NAMES.GLAD_CUSTOMER_PAGE}:n/a:*`,
                            eventStrings: [anaConsts.Event.EVENT_220],
                            actionInfo: anaConsts.LinkData.CUSTOMER_CHAT_SESSION_STARTED
                        };
                        callType = anaConsts.LINK_TRACKING_EVENT;

                        break;
                    default:
                        break;
                }

                if (eventData) {
                    this.fireAnalytics(eventData, callType);
                }
            }

            if (data.gladChat.entryFlag) {
                this.handleShow();
            }

            if (!current?.isChatOpen && data.gladChat.isChatOpen) {
                this.setStorageItem({ isChatOpen: true }, LOCAL_STORAGE.GLADLY_CHAT);
            }
        });
        store.setAndWatch('page', this, data => {
            const {
                page: { showLoadSpaPageProgress }
            } = data;
            const element = this.gladRef.current;
            const isBasketPage = Location.isBasketPage();
            const height = this.basketCheckoutHeightSmui(isBasketPage);

            if (height) {
                if (!showLoadSpaPageProgress) {
                    element.style.setProperty('--custom-bottom-offset', `calc(var(--bottomNavHeight, 0px) + ${height + space[3]}px)`);
                    element.classList.add('chatButtonBasket');
                }
            } else {
                element.classList.remove('chatButtonBasket');
            }
        });

        store.setAndWatch('user', this, () => {
            const userData = this.getStorageItem(LOCAL_STORAGE.USER_DATA);
            this.setUserData(userData);
        });
        try {
            Gladly?.on('conversation:started', started => {
                if (started) {
                    clearTimeout(this.conversationTimeout);
                    this.conversationTimeout = null;
                    this.setStorageItem({ conversationEnd: null }, LOCAL_STORAGE.GLADLY_CHAT);
                }
            });

            Gladly?.on('conversation:ended', ended => {
                if (ended) {
                    const conversationEnd = Date.now();
                    this.setStorageItem({ conversationEnd }, LOCAL_STORAGE.GLADLY_CHAT);
                    this.timeOutHandler(conversationEnd);

                    if (this.gladRef.current?.classList?.contains('gladly-show')) {
                        const unreadMessage = this.state.unreadMessage + 1;
                        this.setState({
                            unreadMessage
                        });
                        this.setStorageItem({ unreadMessage }, LOCAL_STORAGE.GLADLY_CHAT);
                    }

                    this.fireAnalytics(
                        {
                            actionInfo: anaConsts.LinkData.CUSTOMER_CHAT_SESSION_ENDED,
                            eventStrings: [anaConsts.Event.EVENT_221]
                        },
                        anaConsts.LINK_TRACKING_EVENT
                    );
                }
            });

            Gladly?.on('message:received', msg => {
                if (msg && this.gladRef.current?.classList?.contains('gladly-show')) {
                    const unreadMessage = this.state.unreadMessage + 1;
                    this.setState({
                        unreadMessage
                    });
                    this.setStorageItem({ unreadMessage }, LOCAL_STORAGE.GLADLY_CHAT);
                }
            });
        } catch (error) {
            Sephora.logger.error('Gladly_Events_Failed:', error);
        }
    };

    timeOutHandler = conversationEnd => {
        if (!this.conversationTimeout) {
            const diff = Date.now() - conversationEnd;
            const { timer = 0 } = Sephora.configurationSettings.gladChatConfig || {};
            const TIMER = timer * 60 * 1000;
            const remainingTimer = diff > TIMER ? 0 : TIMER - diff;
            this.conversationTimeout = setTimeout(async () => {
                await this.handleClear();
                this.setStorageItem({ conversationEnd: null, unreadMessage: 0 }, LOCAL_STORAGE.GLADLY_CHAT);
                const userData = this.getStorageItem(LOCAL_STORAGE.USER_DATA);
                this.setUserData(userData);
            }, remainingTimer);
        }
    };

    setUserData = async userData => {
        try {
            const gladlyUser = Gladly?.getUser();
            const current = this.getStorageItem(LOCAL_STORAGE.GLADLY_CHAT);

            if (userData?.profile?.login && userData?.profile?.login !== gladlyUser?.identity) {
                await this.handleClear();
            }

            if (!userData?.profile?.login && current?.userAuth) {
                await this.handleClear();
                this.handleClose();
                this.props.updateGladChatState({ isChatOpen: false });
                this.setStorageItem({ userAuth: false, isChatOpen: false, conversationEnd: null, unreadMessage: 0 }, LOCAL_STORAGE.GLADLY_CHAT);
            }

            if (userData?.profile?.firstName && userData?.profile?.login) {
                if (!gladlyUser?.identity) {
                    Gladly?.setUser({ name: userData?.profile?.firstName, email: userData?.profile?.login });
                }

                this.setStorageItem({ userAuth: true }, LOCAL_STORAGE.GLADLY_CHAT);
            }
        } catch (error) {
            Sephora.logger.error('Gladly_User_Failed:', error);
        }
    };

    initializeGladChat = (appId, isChatOpen, conversationEnd) => {
        Gladly?.init({
            appId
        })
            .then(() => {
                this.afterInitilizeGladChat();

                if (!isChatOpen) {
                    this.handleClose();
                }

                if (conversationEnd) {
                    this.timeOutHandler(conversationEnd);
                }
            })
            .catch(function (error) {
                Sephora.logger.error('Gladly_Initialize_Failed:', error);
            });
    };

    handleClear = async () => {
        try {
            await Gladly?.clearUser();
        } catch (error) {
            Sephora.logger.error('Gladly_ClearUser_Failed:', error);
        }
    };

    handleClose = () => {
        if (window.Gladly) {
            try {
                Gladly?.close();
            } catch (error) {
                Sephora.logger.error('Gladly_Close_Failed:', error);
            }
        } else {
            Sephora.logger.error('Gladly_Is_Not_Available_On_Window');
        }
    };

    handleShow = () => {
        if (window.Gladly) {
            try {
                Gladly?.show();
                this.setState({
                    unreadMessage: 0
                });
                this.setStorageItem({ unreadMessage: 0 }, LOCAL_STORAGE.GLADLY_CHAT);
            } catch (error) {
                Sephora.logger.error('Gladly_Show_Failed:', error);
            }
        } else {
            Sephora.logger.error('Gladly_Is_Not_Available_On_Window');
        }
    };

    minimizeButtonAction = () => {
        this.handleShow();
        this.fireAnalytics(
            {
                pageName: `${anaConsts.PAGE_NAMES.GLAD_FOOTER_PAGE}:n/a:*`,
                linkData: anaConsts.LinkData.CUSTOMER_CHAT_SESSION_RESUME //prop55
            },
            anaConsts.ASYNC_PAGE_LOAD
        );
    };

    basketCheckoutHeightSmui = isBasketPage => {
        const dataHeightKey = isBasketPage ? 'bottomCostsummaryHeight' : 'bottomStickyHeight';

        return document.body.querySelector(`[data-height="${dataHeightKey}"]`)?.clientHeight || 0;
    };

    render() {
        const { unreadMessage } = this.state;
        const { isChatOpen } = this.props;
        const btnLabel = getText('buttonLabel');
        const globalStyles = css`
            #custom-gladly-chat-button.gladly-show {
                display: block;
            }
            #custom-gladly-chat-button.gladly-unread .with-unread-message {
                display: flex;
            }
            .chatButtonBasket {
                bottom: var(--custom-bottom-offset) !important;
            }
            ${mediaQueries.md} {
                .chatButtonBasket {
                    bottom: ${space[0]} !important;
                }
            }
        `;

        return (
            <>
                <Global styles={globalStyles} />
                <button
                    id='custom-gladly-chat-button'
                    onClick={this.minimizeButtonAction}
                    css={[styles.chatButton, !isChatOpen && styles.chatButtonHide]}
                    ref={this.gladRef}
                >
                    <div css={[styles.chatBox]}>
                        <Media lessThan='md'>
                            {unreadMessage > 0 ? (
                                <div
                                    className='with-unread-message'
                                    css={styles.redBox}
                                >
                                    {unreadMessage}
                                </div>
                            ) : null}
                            <Icon
                                name='gladChat'
                                size={26}
                            />
                        </Media>
                        <Media
                            greaterThan='md'
                            css={[styles.labelContent]}
                        >
                            {unreadMessage > 0 ? (
                                <div
                                    className='with-unread-message'
                                    css={styles.redBox}
                                ></div>
                            ) : null}
                            <Icon
                                name='gladChat'
                                size={24}
                            />
                            <span css={styles.chatLabel}>{btnLabel}</span>
                            <Icon
                                name={'caretUp'}
                                size={24}
                            />
                        </Media>
                    </div>
                </button>
            </>
        );
    }
}

const styles = {
    chatButton: {
        position: 'fixed',
        display: 'none',
        flexDirection: 'column',
        alignItems: 'flex-start',
        right: space[3],
        backgroundColor: colors.black,
        color: colors.white,
        bottom: `calc(var(--bottomNavHeight, 0px) + ${space[3]}px)`,
        borderRadius: radii.full,
        padding: space[3],
        zIndex: 1,
        [mediaQueries.md]: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
            padding: `${space[3]}px ${space[4]}px`,
            bottom: space[0]
        }
    },
    chatButtonHide: {
        display: 'none !important'
    },
    labelContent: {
        display: 'flex',
        alignItems: 'center',
        gap: space[2]
    },
    chatBox: {
        display: 'flex',
        alignItems: 'center'
    },
    chatLabel: {
        textAlign: 'center',
        fontSize: fontSizes.md,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight
    },
    redBox: {
        width: 20,
        height: 20,
        backgroundColor: colors.red,
        borderRadius: radii.full,
        position: 'absolute',
        justifyContent: 'center',
        top: space[1],
        left: space[5],
        zIndex: 1,
        display: 'none',
        alignItems: 'center',
        fontSize: fontSizes.sm,
        fontWeight: fontWeights.bold
    }
};

export default wrapComponent(GladChat, 'GladChat');
